import {ChangeEvent, useCallback, useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import {setVerifyEmailModalOpen} from 'entities/app'
import {ChangeEmail, ChangePassword, setUserEmail, useUser, VerifyEmail} from 'entities/user'
import {isMailValid, isPasswordValid} from 'shared/lib'
import {Button} from 'shared/ui/buttons'
import {Mail, Verified} from 'shared/ui/icons'
import {createSnackbar} from 'shared/ui/snackbar'
import {TextField} from 'shared/ui/textField'
import styles from './styles.module.scss'

export const Account = () => {
  const dispatch = useDispatch()
  const {profile} = useUser()
  const [fields, setFields] = useState({email: profile.email, password: '', confirmPassword: ''})
  const [loadingState, setLoadingState] = useState({email: false, password: false})
  const [errors, setErrors] = useState({email: '', password: ''})
  const [isSkipAutocomplete, setIsSkipAutocomplete] = useState(false)

  const onChangeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>, name: string) => {
      if (!isSkipAutocomplete) return
      e.preventDefault()

      setErrors({email: '', password: ''})
      setFields((prevState) => ({...prevState, [name]: e.target.value.trim()}))
    },
    [setFields, isSkipAutocomplete]
  )

  const onChangeVerifyHandler = useCallback(
    (name: string) => {
      if (loadingState.email) return

      setLoadingState((prevState) => ({...prevState, [name]: true}))

      VerifyEmail()
        .then(() => dispatch(setVerifyEmailModalOpen(true)))
        .catch(console.error)
        .finally(() => setLoadingState((prevState) => ({...prevState, [name]: false})))
    },
    [setLoadingState, loadingState.email]
  )

  const onSaveHandler = useCallback(
    () => {
      if (!isMailValid(fields.email.trim()))
        return setErrors(prevState => ({...prevState, email: 'Invalid email'}))
      if (loadingState.email) return

      setLoadingState((prevState) => ({...prevState, email: true}))

      ChangeEmail(fields.email)
        .then(() => dispatch(setUserEmail(fields.email)))
        .catch((e) => {
          setErrors(prevState => ({...prevState, email: e.error === 'email_taken' ? 'Email already taken' : e.error}))
        })
        .finally(() => setLoadingState((prevState) => ({...prevState, email: false})))
    },
    [setErrors, isMailValid, fields.email, loadingState.email]
  )

  const changePasswordHandler = useCallback(
    () => {
      if (!fields.password || !fields.confirmPassword) return

      if (!isPasswordValid(fields.password) || !isPasswordValid(fields.confirmPassword))
        return setErrors((prevState) => ({...prevState, password: 'Minimum password length must be 6 characters'}))

      if (fields.password !== fields.confirmPassword)
        return setErrors((prevState) => ({...prevState, password: 'Password do not match'}))

      if (loadingState.password) return

      setLoadingState((prevState) => ({...prevState, password: true}))

      ChangePassword(fields.password)
        .then(() => {
          setFields((prevState) => ({...prevState, password: '', confirmPassword: ''}))
          createSnackbar({text: 'The password has been changed'})
        })
        .catch((e) => {
          setErrors(prevState => ({...prevState, password: e.error}))
        })
        .finally(() => setLoadingState((prevState) => ({...prevState, password: false})))
    },
    [fields.password, fields.confirmPassword, loadingState.password]
  )

  useEffect(() => {
    if (isSkipAutocomplete) return

    setTimeout(() => {
      setIsSkipAutocomplete(true)
    }, 1000)

    return () => setIsSkipAutocomplete(false)
  }, [])

  return (
    <div className={styles.wrapper}>
      <div className={styles.verificationItem}>
        <div className={styles.verificationItemContent}>
          <div className={styles.mailIconContainer}>
            <div className={`${styles.mailIcon} ${profile.isConfirmed ? styles.mailIcon_active : ''}`}>
              <Mail />
            </div>
          </div>
          <div className={styles.textWrapper}>
            <div className={styles.title}>
              {profile.isConfirmed ? <div className={styles.verifyIcon}> <Verified /> </div> : null}
              {profile.isConfirmed ? 'Verified' : 'Verify'} Email
            </div>
            <span className={styles.description}>
            Confirm email makes your account more secure and you will be able to receive all important messages
            </span>
          </div>
        </div>

        {!profile.isConfirmed ?
          <Button
            onClick={() => onChangeVerifyHandler('email')}
            styleType='primary'
            className={styles.btn}
          >
            Verify
          </Button>
          : null}
      </div>

      <TextField
        value={fields.email}
        onChange={(e) => onChangeHandler(e, 'email')}
        label='Email'
        type='email'
        placeholder='Email'
        error={errors.email}
      />
      {fields.email !== profile.email ?
        <Button
          isLoading={loadingState.email}
          isDisabled={loadingState.email}
          styleType='primary'
          onClick={onSaveHandler}
          className={styles.btn}
        >
          Save
        </Button>
        : null}
      <TextField
        type='password'
        withoutAutoComplete
        label='New password'
        placeholder='New password'
        value={fields.password}
        error={errors.password}
        onChange={(e) => onChangeHandler(e, 'password')}
      />
      <TextField
        type='password'
        label='Confirm new password'
        placeholder='Confirm new password'
        value={fields.confirmPassword}
        error={errors.password}
        onChange={(e) => onChangeHandler(e, 'confirmPassword')}
      />
      <Button
        isLoading={loadingState.password}
        styleType='primary'
        onClick={changePasswordHandler}
        className={styles.btn}
        isDisabled={!fields.password || !fields.confirmPassword || loadingState.password}
      >
        Change password
      </Button>
    </div>
  )
}